<template>
  <div class="developer-form" v-if="item">
    <div class="developer-form__tabs-shell">
      <v-tabs v-model="activeTab" background-color="transparent" class="developer-form__tabs">
        <v-tab>
          <v-icon left small>mdi-account-tie</v-icon>
          Developer
        </v-tab>
        <v-tab :disabled="!item.id">
          <v-icon left small>mdi-gamepad-variant</v-icon>
          Games
        </v-tab>
      </v-tabs>

      <transition name="developer-tab-slide" mode="out-in">
        <div v-if="activeTab === 0" key="tab-developer" class="developer-form__tab-panel">
          <div class="developer-form__tab-panel-body developer-form__tab-panel-body--padded">
            <v-form v-model="valid" class="developer-form__main">
              <v-card class="mb-4 developer-form__section" elevation="1">
                <v-card-title class="d-flex align-center developer-form__section-title">
                  <v-icon class="mr-2">mdi-account-tie</v-icon>
                  <span class="font-weight-bold">Basic attributes</span>
                </v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="item.name"
                        label="Name *"
                        :rules="[rules.required]"
                        dense
                        hide-details="auto"
                        data-testid="developer-form-name"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="item.azureAdId"
                        label="Azure Ad Id (studio name) *"
                        :rules="[rules.required]"
                        dense
                        hide-details="auto"
                        data-testid="developer-form-azure-ad-id"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <div class="developer-form__save mb-2">
                <v-btn
                  color="primary"
                  large
                  class="mr-2"
                  :disabled="!valid"
                  data-testid="developer-form-save"
                  @click="save"
                >
                  {{ item.id ? 'Save' : 'Create' }}
                </v-btn>
                <v-btn
                  outlined
                  large
                  :to="'/developers'"
                  data-testid="developer-form-cancel"
                >
                  Cancel
                </v-btn>
              </div>
            </v-form>
          </div>
        </div>

        <div v-else-if="activeTab === 1" key="tab-games" class="developer-form__tab-panel">
          <div class="developer-form__tab-panel-body">
            <v-card class="mb-0 developer-form__games" elevation="1">
              <v-card-title class="d-flex align-center">
                <v-icon class="mr-2">mdi-gamepad-variant</v-icon>
                <span class="font-weight-bold">Games</span>
                <v-spacer />
                <v-dialog v-model="dialog" width="800" persistent>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="success"
                      data-testid="developer-assign-games"
                      v-bind="attrs"
                      v-on="on"
                    >
                      Assign
                    </v-btn>
                  </template>
                  <v-card class="admin-dialog">
                    <v-card-title class="d-flex align-center admin-dialog__title">
                      <span class="font-weight-bold">
                        Assign games to developer "{{ item.name }}" (#{{ item.id }})
                      </span>
                      <v-spacer />
                      <v-btn icon small @click="closeAssignDialog">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text class="admin-dialog__body">
                      <v-text-field
                        v-model="gamesSearch"
                        label="Search"
                        dense
                        outlined
                        hide-details
                        class="mb-3"
                        prepend-inner-icon="mdi-magnify"
                      />
                      <v-data-table
                        class="admin-dialog__table"
                        dense
                        show-select
                        sort-by="id"
                        :search="gamesSearch"
                        :headers="gamesHeaders"
                        :items="absentGames"
                        v-model="selectedGames"
                        :items-per-page="10"
                        :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
                      />
                    </v-card-text>
                    <v-card-actions class="admin-dialog__actions">
                      <v-spacer />
                      <v-btn
                        color="primary"
                        class="mr-2"
                        :disabled="!selectedGames.length"
                        data-testid="developer-assign-games-save"
                        @click="addGames"
                      >
                        Save
                      </v-btn>
                      <v-btn outlined @click="closeAssignDialog">Cancel</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-card-title>

              <v-card-text class="developer-form__games-search py-1">
                <v-text-field
                  v-model="search"
                  label="Search"
                  dense
                  hide-details
                  class="mt-0"
                  prepend-icon="mdi-magnify"
                />
              </v-card-text>

              <v-data-table
                class="developer-form__games-table"
                :loading="!item"
                sort-by="id"
                :search="search"
                :headers="developerGamesHeaders"
                :items="item.games || []"
                :items-per-page="10"
                :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
              >
                <template v-slot:item.actions="{ item: game }">
                  <div class="developer-form__game-actions">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          :data-testid="`developer-open-game-${game.id}`"
                          @click="$router.push(`/games/${game.id}`)"
                        >
                          mdi-pencil
                        </v-icon>
                      </template>
                      <span>Edit game</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          color="warning"
                          v-bind="attrs"
                          v-on="on"
                          :data-testid="`developer-delete-game-${game.id}`"
                          @click="deleteItem(game)"
                        >
                          mdi-delete
                        </v-icon>
                      </template>
                      <span>Удалить</span>
                    </v-tooltip>
                  </div>
                </template>
                <template v-slot:no-data>
                  <div class="text-center pa-6">
                    <v-icon size="64" color="grey lighten-1">mdi-gamepad-variant-outline</v-icon>
                    <p class="mt-2 mb-0 grey--text">No games assigned yet. Assign a game to get started.</p>
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import persistentDialogEsc from '@/mixins/persistentDialogEsc';

const DEVELOPER_TABS = ['developer', 'games'];

function tabIndexFromQuery(tab, allowSecondaryTabs) {
  const key = String(tab || 'developer').toLowerCase();
  const index = DEVELOPER_TABS.indexOf(key);
  if (index <= 0) return 0;
  return allowSecondaryTabs ? index : 0;
}

function tabNameFromIndex(index) {
  return DEVELOPER_TABS[index] || 'developer';
}

export default {
  name: 'DeveloperForm',
  mixins: [
    persistentDialogEsc({
      dialog: 'closeAssignDialog',
    }),
  ],
  props: {
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeTab: 0,
      dialog: false,
      search: '',
      gamesSearch: '',
      valid: true,
      games: [],
      selectedGames: [],
      gamesHeaders: [
        { text: 'Id', value: 'id', width: '72px' },
        { text: 'Slug', value: 'slug' },
        { text: 'Name', value: 'name' },
      ],
      developerGamesHeaders: [
        { text: 'Id', value: 'id' },
        { text: 'Slug', value: 'slug' },
        { text: 'Name', value: 'name' },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '1%',
          class: 'developer-form__games-col--compact',
          cellClass: 'developer-form__games-col--compact',
        },
      ],
      rules: {
        required: (v) => (v !== null && v !== undefined && String(v).trim() !== '') || 'Required',
      },
    };
  },
  computed: {
    absentGames() {
      const assigned = new Set((this.item.games || []).map((g) => Number(g.id)));
      return this.games.filter((g) => !assigned.has(Number(g.id)));
    },
  },
  watch: {
    'item.id': {
      immediate: true,
      handler() {
        this.syncTabFromRoute();
      },
    },
    '$route.query.tab'() {
      this.syncTabFromRoute();
    },
    activeTab(index) {
      this.syncTabToRoute(index);
    },
  },
  async created() {
    this.syncTabFromRoute();
    this.games = await this.$gameApi.get('/games');
  },
  methods: {
    syncTabFromRoute() {
      const allowSecondaryTabs = !!this.item?.id;
      const next = tabIndexFromQuery(this.$route.query.tab, allowSecondaryTabs);
      if (this.activeTab !== next) {
        this.activeTab = next;
      }
    },
    syncTabToRoute(index) {
      if (!this.item?.id && index !== 0) {
        this.activeTab = 0;
        return;
      }
      const tab = tabNameFromIndex(index);
      const current = String(this.$route.query.tab || 'developer').toLowerCase();
      if (current === tab) return;
      this.$router.replace({
        query: {
          ...this.$route.query,
          tab,
        },
      }).catch(() => {});
    },
    closeAssignDialog() {
      this.dialog = false;
      this.selectedGames = [];
      this.gamesSearch = '';
    },
    async save() {
      if (!this.item.name || !this.item.azureAdId) {
        this.$swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Name and Azure Ad Id are required fields',
        });
        return;
      }
      const body = { role: this.item.role, name: this.item.name, azureAdId: this.item.azureAdId };
      if (this.item.id) {
        await this.$gameApi.patch(`/users/${this.item.id}`, body);
        this.$swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Developer has been updated successfully',
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        const newItem = await this.$gameApi.post(`/users/`, body);
        this.$swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Developer has been created successfully',
          timer: 1500,
          showConfirmButton: false,
        });
        this.$router.push(`/developers/${newItem.id}`);
      }
    },
    async deleteItem(game) {
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: 'This action will assign a game to Arkadium developer. Are you sure to proceed?',
        showCancelButton: true,
      });
      if (!isConfirmed) return;
      const developers = await this.$gameApi.get(`/users?name=Arkadium`);
      if (developers.length) {
        const developerId = developers.find((d) => true).id;
        await this.$gameApi.patch(`/games/${game.id}`, { developerId });
        this.$emit('change', this.item);
      } else {
        this.$swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Arkadium developer not found!',
        });
      }
    },
    async addGames() {
      await Promise.all(
        this.selectedGames.map((g) =>
          this.$gameApi.patch(`/games/${g.id}`, { developerId: this.item.id }),
        ),
      );
      this.$emit('change', this.item);
      this.closeAssignDialog();
    },
  },
};
</script>

<style scoped>
.developer-form {
  width: 100%;
  align-self: flex-start;
}

.developer-form__tabs-shell {
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 16px;
  height: fit-content;
  align-self: flex-start;
}

.developer-form__tabs {
  border: none !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
  border-radius: 0 !important;
  background: #f5f5f5 !important;
  margin: 0 !important;
}

.developer-form__tab-panel {
  margin: 0;
  border: none;
  border-radius: 0;
  background: #fff;
  /* Short Developer tab must not stretch — blue accent follows section height */
  min-height: 0;
  height: fit-content;
  box-shadow: none;
}

.developer-form__tab-panel-body {
  padding: 0;
  overflow-x: auto;
  height: fit-content;
}

.developer-form__tab-panel-body--padded {
  padding: 16px;
}

/* Flush only full-bleed grids (Games tab), not padded attribute sections */
.developer-form__tab-panel-body:not(.developer-form__tab-panel-body--padded) ::v-deep > .v-card {
  margin: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  border: none !important;
}

.developer-form__main {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: auto;
}

.developer-form__section {
  border-left: 4px solid #1976d2;
  overflow: hidden;
  flex: 0 0 auto;
  height: fit-content !important;
  align-self: stretch;
}

.developer-form__section-title {
  display: flex;
  align-items: center;
  padding-top: 16px !important;
  padding-bottom: 12px !important;
  font-size: 1.15rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
}

.developer-form__section ::v-deep > .v-card-text {
  padding-top: 4px !important;
  padding-bottom: 12px !important;
  flex: none !important;
}

.developer-form__section ::v-deep .row--dense > .col,
.developer-form__section ::v-deep .row--dense > [class*='col-'] {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

/* Short form: keep Save under the section (sticky bottom was pinning it to the panel foot) */
.developer-form__save {
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  flex: 0 0 auto;
  position: relative;
  z-index: 2;
  margin-top: 0;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(25, 118, 210, 0.2);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(6px);
}

.developer-form__games {
  overflow: hidden;
}

.developer-form__games-search {
  max-width: 420px;
}

.developer-form__game-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.developer-form__games-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.developer-form__games-table ::v-deep .developer-form__games-col--compact {
  white-space: nowrap;
  width: 1%;
  padding-left: 8px;
  padding-right: 8px;
}

.developer-tab-slide-enter-active,
.developer-tab-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.developer-tab-slide-enter {
  opacity: 0;
  transform: translateX(24px);
}

.developer-tab-slide-leave-to {
  opacity: 0;
  transform: translateX(-24px);
}
</style>
